import { Flag, ObfuscatedFlag } from '@eppo/js-client-sdk';
import { useEffect, useRef } from 'react';

import { useEppoStore } from './eppoStore';
import BigPictureAssignmentLogger from './logging/BigPictureAssignmentLogger';
import { InitializationStates } from './types';

export default function useEppo({
  flagsConfiguration,
  bypassInitialization = false,
}: {
  flagsConfiguration?: Record<string, Flag | ObfuscatedFlag>;
  bypassInitialization?: boolean;
}) {
  const { initializationState, setInitializationState } = useEppoStore();
  const initializationStateRef = useRef(initializationState);

  useEffect(() => {
    if (
      initializationStateRef.current === InitializationStates.INITIALIZED ||
      (bypassInitialization && initializationStateRef.current === InitializationStates.BYPASSED)
    )
      return;

    if (bypassInitialization || !flagsConfiguration) {
      initializationStateRef.current = InitializationStates.BYPASSED;
      setInitializationState(InitializationStates.BYPASSED);
      return;
    }

    (async () => {
      if (initializationStateRef.current === InitializationStates.INITIALIZING) return;
      initializationStateRef.current = InitializationStates.INITIALIZING;
      try {
        const { offlineInit } = await import('@eppo/js-client-sdk');
        offlineInit({
          flagsConfiguration,
          assignmentLogger: new BigPictureAssignmentLogger(),
          overridesStorageKey: 'eppo-overrides',
          enableOverrides: true,
        });
        setInitializationState(InitializationStates.INITIALIZED);
        initializationStateRef.current = InitializationStates.INITIALIZED;
      } catch (error) {
        setInitializationState(InitializationStates.BYPASSED);
        initializationStateRef.current = InitializationStates.BYPASSED;
      }
    })();
  }, [bypassInitialization, setInitializationState, flagsConfiguration]);

  return {
    initializationState,
    isInitialized: initializationState === InitializationStates.INITIALIZED,
  };
}
